import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { ThemeService, PurchasePortalTheme } from './theme.service';
import { COMPANY_INFO } from '../../state/app.selector';

@Injectable({
  providedIn: 'root',
})
export class ThemeInitService {
  private readonly themeService = inject(ThemeService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly destroy$ = new Subject<void>();

  constructor() {
    // Only initialize on browser side
    if (isPlatformBrowser(this.platformId)) {
      // Apply default theme immediately to prevent flash of unstyled content
      this.themeService.initializeThemeWhenReady(undefined, undefined);
      this.initializeThemeOnRouteChange();
      this.initializeThemeOnCompanyLoad();
    }
  }

  /**
   * Initialize theme when route changes (to capture query parameters)
   */
  private initializeThemeOnRouteChange(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        // Get current route's query parameters
        const queryParams = this.getCurrentQueryParams();

        // Get company theme from store
        this.store
          .select(COMPANY_INFO)
          .pipe(takeUntil(this.destroy$))
          .subscribe((companyInfo) => {
            if (companyInfo?.compDetails) {
              const companyTheme = this.parseCompanyTheme(
                companyInfo.compDetails.purchase_portal_theme,
              );
              this.themeService.initializeThemeWhenReady(
                companyTheme,
                queryParams,
              );
            } else {
              // Apply only query params if no company info yet
              this.themeService.initializeThemeWhenReady(
                undefined,
                queryParams,
              );
            }
          });
      });
  }

  /**
   * Initialize theme when company information is loaded
   */
  private initializeThemeOnCompanyLoad(): void {
    this.store
      .select(COMPANY_INFO)
      .pipe(
        filter((companyInfo) => !!companyInfo?.compDetails?.uuid), // Only when company is actually loaded
        takeUntil(this.destroy$),
      )
      .subscribe((companyInfo) => {
        const queryParams = this.getCurrentQueryParams();
        const companyTheme = this.parseCompanyTheme(
          companyInfo.compDetails.purchase_portal_theme,
        );
        this.themeService.initializeThemeWhenReady(companyTheme, queryParams);
      });
  }

  /**
   * Get current route query parameters
   */
  private getCurrentQueryParams(): any {
    // Only access window on browser side
    if (!isPlatformBrowser(this.platformId) || typeof window === 'undefined') {
      return {};
    }

    // Parse current URL query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const queryParams: any = {};

    for (const [key, value] of urlParams.entries()) {
      queryParams[key] = value;
    }

    return queryParams;
  }

  /**
   * Parse company purchase_portal_theme (can be JSON string or direct object)
   */
  private parseCompanyTheme(
    theme?: string | PurchasePortalTheme,
  ): PurchasePortalTheme | undefined {
    if (!theme) return undefined;

    // If it's already an object, return it directly
    if (typeof theme === 'object') {
      return theme;
    }

    // If it's a string, parse it as JSON
    if (typeof theme === 'string') {
      const parsed = this.themeService.parsePurchasePortalTheme(theme);
      return parsed || undefined;
    }

    return undefined;
  }

  /**
   * Manually trigger theme application (useful for components that need to force theme update)
   */
  public applyThemeManually(
    companyTheme?: PurchasePortalTheme,
    queryParams?: any,
  ): void {
    this.themeService.initializeThemeWhenReady(companyTheme, queryParams);
  }

  /**
   * Cleanup subscriptions
   */
  public destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
